import { equals } from 'ramda';
import { useRef, useMemo, useEffect } from 'react';
import { VariableSizeList } from 'react-window';

import { usePrevious, useResizeObserver } from 'common/hooks';

import { MENU_MOBILE_BREAKPOINT } from '../../constants';
import { SharedMenuElement } from '../../types';

export const useIsNarrowView = () => {
  const headerRef = useRef<HTMLDivElement>(null);
  const { width } = useResizeObserver(headerRef);
  // /* 24px account for the left navigation when collapsed */
  const isNarrowView = useMemo(() => width !== undefined && width <= MENU_MOBILE_BREAKPOINT - 24, [width]);

  return { headerRef, isNarrowView };
};

export const getItemKey = (index: number, itemList: SharedMenuElement[]) => {
  const row = itemList[index];

  if (row.type === 'item') {
    return row.itemState.id;
  }
  if (row.type === 'option') {
    return row.optionState.id;
  }
  if (row.type === 'options-subheader') {
    return `row-options-subheader-${index}`;
  }
  return row.category ? `row-category-${row.category.id}` : `row-category-${index}`;
};

export const useVirtualListHeights = (itemList: SharedMenuElement[]) => {
  const listRef = useRef<VariableSizeList>(null);

  const virtualListHeights = useMemo(
    () =>
      itemList.map(el =>
        el.type === 'category-subheader' || el.type === 'options-subheader' || el.type === 'subcategory-subheader'
          ? 64
          : 105,
      ),
    [itemList],
  );
  const previousVirtualListHeights = usePrevious(virtualListHeights);

  useEffect(() => {
    if (!equals(virtualListHeights, previousVirtualListHeights)) {
      /* Forces recalculating heights after dragging items around */
      listRef.current?.resetAfterIndex(0);
    }
  }, [previousVirtualListHeights, virtualListHeights]);

  return {
    virtualListHeights,
    listRef,
  };
};

export const useFocusInputOnCtrlF = () => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.code === 'KeyF') {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return { inputRef };
};

export const getCategoryBulkSelectionStatus = (offerIds: string[], selectedIds?: Set<string>) => {
  if (offerIds.length === 0 || selectedIds?.size === 0) {
    return 'not-selected';
  }
  if (offerIds.every(id => selectedIds?.has(id))) {
    return 'selected';
  }
  if (offerIds.some(id => selectedIds?.has(id))) {
    return 'partially-selected';
  }
  return 'not-selected';
};
